import { useId, type ReactNode } from 'react';
import { METHOD_LABEL, STATUS_LABEL } from '../format';
import { PAYMENT_METHODS, RECORD_STATUSES } from '../types';
import type { RecordFilters } from '../query';
import { CaretDownIcon } from './icons';

export interface ContractorOption { id: string; name: string }

interface Props {
  filters: RecordFilters;
  searchText: string;
  contractors: ContractorOption[];
  onSearch: (text: string) => void;
  onChange: <K extends keyof RecordFilters>(key: K, value: RecordFilters[K]) => void;
  disabled: boolean;
  /** The action buttons (Record Outside Payment first, then Email and Export): one group that wraps as a unit. */
  actions: ReactNode;
}

function DateField({ label, id, value, onChange, disabled }: { label: string; id: string; value: string; onChange: (v: string) => void; disabled: boolean }) {
  return (
    <div className="cp-field cp-field--date">
      <label className="cp-field__label" htmlFor={id}>{label}</label>
      <input id={id} type="date" className="cp-input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function RangeField({ label, from, to, onFrom, onTo, disabled, ids }: {
  label: string; from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void; disabled: boolean; ids: [string, string];
}) {
  return (
    <fieldset className="cp-field cp-field--range">
      <legend className="cp-field__label">{label}</legend>
      <div className="cp-range">
        <label className="cp-visually-hidden" htmlFor={ids[0]}>{label}, from</label>
        <input id={ids[0]} type="date" className="cp-input" value={from} max={to || undefined} disabled={disabled} onChange={(e) => onFrom(e.target.value)} />
        <span aria-hidden="true">–</span>
        <label className="cp-visually-hidden" htmlFor={ids[1]}>{label}, to</label>
        <input id={ids[1]} type="date" className="cp-input" value={to} min={from || undefined} disabled={disabled} onChange={(e) => onTo(e.target.value)} />
      </div>
    </fieldset>
  );
}

function Select({ label, value, onChange, disabled, children }: { label: string; value: string; onChange: (v: string) => void; disabled: boolean; children: React.ReactNode }) {
  const id = useId();
  return (
    <div className="cp-select">
      <label className="cp-visually-hidden" htmlFor={id}>{label}</label>
      <select id={id} className="cp-pill" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{label}</option>
        {children}
      </select>
      <span className="cp-select__caret" aria-hidden="true"><CaretDownIcon /></span>
    </div>
  );
}

/** The Figma filter rows: Payment Date, Select Service Period, Date Range; then search, Contractor Name, Status, 1099 Trackable, Pay Method. The server filters. */
export function FilterBar({ filters, searchText, contractors, onSearch, onChange, disabled, actions }: Props) {
  const uid = useId();
  return (
    <div className="cp-filters cp-toolbar" role="search" aria-label="Filter contractor payments">
      <div className="cp-toolbar__actions">{actions}</div>
      <div className="cp-filters__dates">
        <DateField label="Payment Date" id={`${uid}-pd`} value={filters.paymentDate} onChange={(v) => onChange('paymentDate', v)} disabled={disabled} />
        <RangeField label="Select Service Period" from={filters.serviceFrom} to={filters.serviceTo} onFrom={(v) => onChange('serviceFrom', v)} onTo={(v) => onChange('serviceTo', v)} disabled={disabled} ids={[`${uid}-sf`, `${uid}-st`]} />
        <RangeField label="Date Range" from={filters.rangeFrom} to={filters.rangeTo} onFrom={(v) => onChange('rangeFrom', v)} onTo={(v) => onChange('rangeTo', v)} disabled={disabled} ids={[`${uid}-rf`, `${uid}-rt`]} />
      </div>
      <div className="cp-filters__refine">
        <div className="cp-search">
          <label className="cp-visually-hidden" htmlFor={`${uid}-search`}>Search</label>
          <input id={`${uid}-search`} type="search" className="cp-pill cp-pill--search" placeholder="Search" maxLength={100} value={searchText} disabled={disabled} onChange={(e) => onSearch(e.target.value)} />
        </div>
        <Select label="Contractor Name" value={filters.contractorId} onChange={(v) => onChange('contractorId', v)} disabled={disabled}>
          {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select label="Status" value={filters.status} onChange={(v) => onChange('status', v as RecordFilters['status'])} disabled={disabled}>
          {RECORD_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </Select>
        <Select label="1099 Trackable" value={filters.trackable} onChange={(v) => onChange('trackable', v as RecordFilters['trackable'])} disabled={disabled}>
          <option value="yes">1099 Trackable</option>
          <option value="no">Not 1099 Trackable</option>
        </Select>
        <Select label="Pay Method" value={filters.method} onChange={(v) => onChange('method', v as RecordFilters['method'])} disabled={disabled}>
          {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
        </Select>
      </div>
    </div>
  );
}
